import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { ObjectViewRenderer } from "components/object-view-renderer";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Pencil, Trash } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import { Slide } from "../slides";
import { SLIDES_FIELDS } from "../slides.form";
import { SLIDES_DELETE, SLIDES_SHOW } from "../slides.gql";

export default function SlidesShow(): React.ReactElement {
  const [, theme] = useStyletron();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(
    false
  );
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { setIsLoading, setIsFetching } = useLoading();

  const { refetch, data, error } = useQuery(SLIDES_SHOW, {
    variables: { id: id ? parseInt(id) : null },
  });
  const slide: Slide = data?.slide;

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  useEffect(() => {
    if (data?.slide) refetch();
    setIsFetching(true);
  }, []);

  useEffect(() => data?.slide && setIsFetching(false), [data]);

  const [deleteSlide] = useMutation(SLIDES_DELETE);

  const onSubmit = async () => {
    setIsLoading(true);

    try {
      await deleteSlide({
        variables: {
          slideDeleteInput: {
            id: id ? parseInt(id) : null,
          },
        },
      });
      enqueueSnackbar({
        message: "Usunięto pomyślnie",
        variant: "success",
      });
      history.push("/slides");
      refetch();
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.slide.read))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={slide?.title}
        labels={["Slajdy", "Wyświetlanie"]}
        goBackOption
        buttons={[
          {
            label: "Edytuj",
            kind: KIND.secondary,
            startEnhancer: <Pencil size={18} />,
            permission: checkPermission(PERMISSIONS.slide.update),
            onClick: () => history.push(`/slides/${id}/update`),
          },
        ]}
        actions={[
          {
            label: "Usuń slajd",
            icon: Trash,
            color: theme.colors.negative,
            onClick: () => setIsConfirmDeleteDialogOpen(true),
            permission: checkPermission(PERMISSIONS.slide.delete),
          },
        ]}
      />
      <Content>
        <ObjectViewRenderer<Slide> fields={SLIDES_FIELDS} data={slide} />

        <ConfirmDialog
          isOpen={isConfirmDeleteDialogOpen}
          label={`Usunięcie slajdu ${slide?.title}`}
          close={() => setIsConfirmDeleteDialogOpen(false)}
          confirm={onSubmit}
        />
      </Content>
    </article>
  );
}
